import type { ComparisonPreset, DateRange } from "@/types/trade"
import { endOfLocalDay, startOfLocalDay } from "@/utils/calendarWeekRanges"

/** Comparison period presets shown when Statistics comparison mode is on. */
export const COMPARISON_PRESET_OPTIONS: ReadonlyArray<{
  id: Exclude<ComparisonPreset, "custom">
  label: string
}> = [
  { id: "previous-period-days", label: "Previous period" },
  {
    id: "previous-period-dow",
    label: "Previous period (match day of week)",
  },
  { id: "previous-month", label: "Previous month" },
  { id: "previous-year", label: "Previous year" },
] as const

const MS_PER_LOCAL_DAY = 24 * 60 * 60 * 1000

/** Inclusive local calendar days between two instants (e.g. Mon–Tue → 2). */
export function inclusiveLocalCalendarDayCount(start: Date, end: Date): number {
  const startDay = startOfLocalDay(start)
  const endDay = startOfLocalDay(end)
  return Math.max(
    1,
    Math.round((endDay.getTime() - startDay.getTime()) / MS_PER_LOCAL_DAY) + 1
  )
}

/**
 * Derive the comparison window from the primary range and a comparison preset
 * (same rules as the legacy Chakra DateRangePicker / Google Analytics).
 */
export function computeComparisonRange(
  main: DateRange,
  preset: ComparisonPreset
): DateRange | null {
  if (!main.startDate || !main.endDate || preset === "custom") {
    return null
  }

  const start = startOfLocalDay(main.startDate)
  const end = endOfLocalDay(main.endDate)
  const spanDays = inclusiveLocalCalendarDayCount(start, end)

  let compStart: Date
  let compEnd: Date

  if (preset === "previous-period-days") {
    compEnd = new Date(start)
    compEnd.setDate(compEnd.getDate() - 1)
    compEnd = endOfLocalDay(compEnd)
    compStart = new Date(compEnd)
    compStart.setDate(compStart.getDate() - (spanDays - 1))
    compStart = startOfLocalDay(compStart)
  } else if (preset === "previous-period-dow") {
    compStart = new Date(start)
    compStart.setDate(compStart.getDate() - 7)
    compStart = startOfLocalDay(compStart)
    compEnd = new Date(end)
    compEnd.setDate(compEnd.getDate() - 7)
    compEnd = endOfLocalDay(compEnd)
  } else if (preset === "previous-month") {
    const prevMonthStartLast = new Date(start.getFullYear(), start.getMonth(), 0)
    const prevMonthEndLast = new Date(end.getFullYear(), end.getMonth(), 0)
    compStart = new Date(
      start.getFullYear(),
      start.getMonth() - 1,
      Math.min(start.getDate(), prevMonthStartLast.getDate())
    )
    compEnd = new Date(
      end.getFullYear(),
      end.getMonth() - 1,
      Math.min(end.getDate(), prevMonthEndLast.getDate())
    )
    compStart = startOfLocalDay(compStart)
    compEnd = endOfLocalDay(compEnd)
  } else if (preset === "previous-year") {
    const prevYearStartLast = new Date(
      start.getFullYear() - 1,
      start.getMonth() + 1,
      0
    )
    const prevYearEndLast = new Date(end.getFullYear() - 1, end.getMonth() + 1, 0)
    compStart = new Date(
      start.getFullYear() - 1,
      start.getMonth(),
      Math.min(start.getDate(), prevYearStartLast.getDate())
    )
    compEnd = new Date(
      end.getFullYear() - 1,
      end.getMonth(),
      Math.min(end.getDate(), prevYearEndLast.getDate())
    )
    compStart = startOfLocalDay(compStart)
    compEnd = endOfLocalDay(compEnd)
  } else {
    return null
  }

  return { startDate: compStart, endDate: compEnd }
}
